import { useEffect, useRef, useState } from "react";
import { Package, ListChecks, Paperclip, FileDown, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ORDER_STEPS = [
  { key: "brand", label: "Brand identity" },
  { key: "website", label: "Website" },
  { key: "launch", label: "Launch" },
];
const STEP_STATES = ["pending", "in_progress", "done"];
const STEP_STATE_META = {
  pending: { label: "Not started", color: "rgba(246,243,252,0.35)" },
  in_progress: { label: "In progress", color: "#B8A7E8" },
  done: { label: "Done", color: "#8FD8B8" },
};
const ORDER_STATUS_META = {
  reserved: { label: "Reserved", color: "#E8CE8F" },
  active: { label: "Active", color: "#B8A7E8" },
  complete: { label: "Complete", color: "#8FD8B8" },
};

export function OrdersPanel() {
  const [orders, setOrders] = useState(null);
  const [openDeliverables, setOpenDeliverables] = useState(() => new Set());
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null); // {orderId, key}
  const [uploadBusy, setUploadBusy] = useState(false);

  const pickFile = (orderId, key) => {
    setUploadTarget({ orderId, key });
    requestAnimationFrame(() => fileInputRef.current?.click());
  };

  const onFileChosen = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !uploadTarget) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(
        `${API}/api/admin/orders/${uploadTarget.orderId}/deliverables/${uploadTarget.key}/files`,
        { method: "POST", credentials: "include", body: fd }
      );
      if (res.ok) {
        const saved = await res.json();
        setOrders((prev) =>
          prev.map((o) => (o.id === uploadTarget.orderId ? { ...o, files: [...(o.files || []), saved] } : o))
        );
      }
    } finally {
      setUploadBusy(false);
    }
  };

  const deleteFile = async (orderId, fileId) => {
    const res = await fetch(`${API}/api/admin/files/${fileId}`, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, files: (o.files || []).filter((f) => f.id !== fileId) } : o))
      );
    }
  };

  const toggleDeliverables = (id) =>
    setOpenDeliverables((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/orders`, { credentials: "include" });
        setOrders(res.ok ? await res.json() : []);
      } catch {
        setOrders([]);
      }
    })();
  }, []);

  const applyOrder = (updated) =>
    setOrders((prev) =>
      prev
        ? prev.map((o) => (o.id === updated.id ? { ...updated, files: updated.files ?? o.files } : o))
        : prev
    );

  const setOrderStatus = async (id, status) => {
    const res = await fetch(`${API}/api/admin/orders/${id}/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });
    if (res.ok) applyOrder(await res.json());
  };

  const advanceStep = async (order, step) => {
    const next = STEP_STATES[(STEP_STATES.indexOf(order.steps[step]) + 1) % STEP_STATES.length];
    const res = await fetch(`${API}/api/admin/orders/${order.id}/step`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ step, state: next }),
    });
    if (res.ok) applyOrder(await res.json());
  };

  const advanceDeliverable = async (order, item) => {
    const next = STEP_STATES[(STEP_STATES.indexOf(item.state) + 1) % STEP_STATES.length];
    const res = await fetch(`${API}/api/admin/orders/${order.id}/deliverable`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ key: item.key, state: next }),
    });
    if (res.ok) applyOrder(await res.json());
  };

  if (orders === null) return <p className="text-sm text-paper/45">Loading…</p>;

  return (
    <div data-testid="admin-orders-panel">
      <input
        ref={fileInputRef}
        type="file"
        data-testid="deliverable-file-input"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={onFileChosen}
      />
      {orders.length === 0 ? (
        <div data-testid="admin-orders-empty" className="rounded-3xl border border-white/10 bg-card p-12 text-center">
          <Package className="mx-auto h-9 w-9 text-paper/30" aria-hidden="true" />
          <p className="mt-4 text-sm text-paper/50">No package orders yet.</p>
        </div>
      ) : (
        <ul data-testid="admin-orders-list" className="space-y-4">
          {orders.map((order) => {
            const statusMeta = ORDER_STATUS_META[order.status] || ORDER_STATUS_META.reserved;
            return (
              <li key={order.id} data-testid={`admin-order-${order.id}`} className="rounded-2xl border border-white/[0.08] bg-card p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-display text-base font-extrabold text-paper">{order.package_name}</p>
                    <p className="mt-0.5 text-sm text-paper/55">
                      {order.user_email} ·{" "}
                      {new Date(order.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <span
                      data-testid={`admin-order-status-${order.id}`}
                      className="rounded-full px-3.5 py-1 text-xs font-bold uppercase tracking-[0.12em]"
                      style={{ backgroundColor: `${statusMeta.color}1f`, color: statusMeta.color }}
                    >
                      {statusMeta.label}
                    </span>
                    {order.status === "reserved" && (
                      <button
                        type="button"
                        data-testid={`admin-order-activate-${order.id}`}
                        onClick={() => setOrderStatus(order.id, "active")}
                        className="rounded-full bg-lilac px-4 py-1.5 text-xs font-bold text-ink transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-lilac-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                      >
                        Mark active
                      </button>
                    )}
                    {order.status === "active" && (
                      <button
                        type="button"
                        data-testid={`admin-order-complete-${order.id}`}
                        onClick={() => setOrderStatus(order.id, "complete")}
                        className="rounded-full border border-white/15 px-4 py-1.5 text-xs font-bold text-paper/70 transition-colors duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                      >
                        Mark complete
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-5 grid gap-2.5 border-t border-white/[0.06] pt-5 sm:grid-cols-3">
                  {ORDER_STEPS.map((step) => {
                    const state = order.steps?.[step.key] || "pending";
                    const meta = STEP_STATE_META[state];
                    return (
                      <div key={step.key} className="flex items-center justify-between gap-3 rounded-xl bg-ink/50 px-4 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-paper/80">{step.label}</p>
                          <p data-testid={`admin-step-state-${step.key}-${order.id}`} className="mt-0.5 text-xs font-bold uppercase tracking-[0.1em]" style={{ color: meta.color }}>
                            {meta.label}
                          </p>
                        </div>
                        <button
                          type="button"
                          data-testid={`admin-step-advance-${step.key}-${order.id}`}
                          onClick={() => advanceStep(order, step.key)}
                          className="shrink-0 rounded-full border border-white/15 px-3 py-1.5 text-[11px] font-bold text-paper/60 transition-colors duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                        >
                          Advance
                        </button>
                      </div>
                    );
                  })}
                </div>
                {(order.deliverables || []).length > 0 && (
                  <div className="mt-4">
                    <button
                      type="button"
                      data-testid={`admin-deliverables-toggle-${order.id}`}
                      aria-expanded={openDeliverables.has(order.id)}
                      onClick={() => toggleDeliverables(order.id)}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-1.5 text-xs font-bold text-paper/55 transition-colors duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                    >
                      <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />
                      Deliverables · {order.delivered_count ?? order.deliverables.filter((d) => d.state === "done").length}/{order.deliverables.length} done
                    </button>
                    {openDeliverables.has(order.id) && (
                      <div data-testid={`admin-deliverables-${order.id}`} className="mt-3 grid gap-2 sm:grid-cols-2">
                        {order.deliverables.map((d) => {
                          const meta = STEP_STATE_META[d.state] || STEP_STATE_META.pending;
                          const itemFiles = (order.files || []).filter((f) => f.deliverable_key === d.key);
                          return (
                            <div
                              key={d.key}
                              data-testid={`admin-deliverable-${d.key}-${order.id}`}
                              className="rounded-xl bg-ink/50 px-4 py-2.5"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate text-xs font-bold text-paper/75">{d.label}</p>
                                  <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: meta.color }}>
                                    {meta.label}
                                  </p>
                                </div>
                                <div className="flex shrink-0 items-center gap-1.5">
                                  <button
                                    type="button"
                                    data-testid={`admin-deliverable-attach-${d.key}-${order.id}`}
                                    aria-label={`Attach a file to ${d.label}`}
                                    disabled={uploadBusy}
                                    onClick={() => pickFile(order.id, d.key)}
                                    className="rounded-full border border-white/15 p-1.5 text-paper/60 transition-colors duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac disabled:opacity-50"
                                  >
                                    <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    data-testid={`admin-deliverable-advance-${d.key}-${order.id}`}
                                    onClick={() => advanceDeliverable(order, d)}
                                    className="rounded-full border border-white/15 px-3 py-1 text-[11px] font-bold text-paper/60 transition-colors duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
                                  >
                                    Advance
                                  </button>
                                </div>
                              </div>
                              {itemFiles.length > 0 && (
                                <ul className="mt-2 space-y-1.5 border-t border-white/[0.05] pt-2">
                                  {itemFiles.map((f) => (
                                    <li key={f.id} data-testid={`admin-file-${f.id}`} className="flex items-center gap-2 text-[11px]">
                                      <FileDown className="h-3 w-3 shrink-0 text-lilac" aria-hidden="true" />
                                      <a
                                        href={`${API}/api/files/${f.id}/download`}
                                        className="min-w-0 flex-1 truncate font-bold text-paper/60 underline-offset-2 transition-colors hover:text-paper hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lilac"
                                      >
                                        {f.filename}
                                      </a>
                                      <button
                                        type="button"
                                        data-testid={`admin-file-delete-${f.id}`}
                                        aria-label={`Remove ${f.filename}`}
                                        onClick={() => deleteFile(order.id, f.id)}
                                        className="shrink-0 rounded-full p-0.5 text-paper/35 transition-colors hover:text-red-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lilac"
                                      >
                                        <X className="h-3 w-3" aria-hidden="true" />
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
